import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cx } from "./cx";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

// Shared overlay behaviour: Escape close, focus trap, body scroll lock, restore
// focus on close. Used by both Modal and Drawer. (Behaviour unchanged.)
function useOverlay(open, onClose, panelRef) {
  // Keep the latest onClose in a ref so a changing function reference (callers
  // routinely pass an inline onClose that's recreated every render) does NOT
  // re-run the effect below. Re-running it would steal focus back to the first
  // focusable element on every keystroke — letting inputs accept only one char.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const prevActive = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        onCloseRef.current?.();
        return;
      }
      if (e.key === "Tab") {
        const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
        if (!nodes || nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);

    const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
    (nodes && nodes[0] ? nodes[0] : panelRef.current)?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
    // Only re-run when the overlay opens/closes. panelRef is a stable ref
    // object; onClose is read via onCloseRef so it intentionally isn't a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

const MODAL_SIZES = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  // A near-full-width workspace modal (the multi-leg route form) — wider than the 700px chat column
  // (the overlay is portaled to <body>, so .app{max-width:700px} doesn't clamp it).
  wide: "max-w-[min(1100px,95vw)]",
};

// Shared chrome so Modal + Drawer match exactly.
const OVERLAY = "fixed inset-0 z-50 bg-primary/40 backdrop-blur-sm animate-fade-in";
const PANEL_HEADER = "flex items-center justify-between gap-4 px-5 py-4 border-b border-border";
const PANEL_FOOTER =
  "flex items-center justify-end gap-3 px-5 py-4 border-t border-border bg-surface/60";
const CLOSE_BTN =
  "shrink-0 -mr-1 rounded-btn p-1.5 text-body/50 transition-colors hover:text-primary hover:bg-surface " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

// ── Peoria dark tone (OPT-IN via tone="peoria") ───────────────────────────────
// Additive: R3 and every default caller keep the light chrome above unchanged.
// Only the charter forms pass tone="peoria" to get the dark panel/header/footer.
const TONES = {
  light: {
    overlay: OVERLAY,
    panel: "bg-white rounded-card border border-border shadow-pop",
    header: PANEL_HEADER,
    footer: PANEL_FOOTER,
    close: CLOSE_BTN,
    title: "text-15 font-semibold text-primary",
    body: "px-5 py-4 overflow-auto text-sm text-body",
  },
  peoria: {
    overlay: "fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-fade-in",
    panel: "bg-peoria-surface rounded-card border border-peoria-border shadow-pop",
    header:
      "flex items-center justify-between gap-4 px-5 py-4 border-b border-peoria-border",
    footer:
      "flex items-center justify-end gap-3 px-5 py-4 border-t border-peoria-border bg-peoria-surface2/60",
    close:
      "shrink-0 -mr-1 rounded-btn p-1.5 text-peoria-muted transition-colors hover:text-peoria-text hover:bg-peoria-surface2 " +
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peoria-red",
    title: "text-15 font-semibold text-peoria-text",
    body: "px-5 py-4 overflow-auto text-sm text-peoria-muted",
  },
};

export function Modal({ open, onClose, title, children, footer, size = "md", tone = "light" }) {
  const panelRef = useRef(null);
  useOverlay(open, onClose, panelRef);
  if (!open) return null;
  const t = TONES[tone] || TONES.light;

  return createPortal(
    <div
      className={cx(t.overlay, "flex items-center justify-center p-4")}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
        className={cx(
          "w-full outline-none flex flex-col max-h-[90vh]",
          t.panel,
          MODAL_SIZES[size]
        )}
      >
        {title && (
          <div className={t.header}>
            <h2 className={t.title}>{title}</h2>
            <button onClick={onClose} aria-label="Close" className={t.close}>
              <X size={18} />
            </button>
          </div>
        )}
        <div className={t.body}>{children}</div>
        {footer && <div className={cx(t.footer, "rounded-b-card")}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

export function Drawer({ open, onClose, title, children, footer, side = "right" }) {
  const panelRef = useRef(null);
  useOverlay(open, onClose, panelRef);
  if (!open) return null;

  return createPortal(
    <div
      className={cx(OVERLAY, "flex")}
      style={{ justifyContent: side === "left" ? "flex-start" : "flex-end" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
        className={cx(
          "h-full w-full max-w-md bg-white shadow-pop outline-none flex flex-col",
          side === "left" ? "border-r border-border" : "border-l border-border"
        )}
      >
        {title && (
          <div className={PANEL_HEADER}>
            <h2 className="text-15 font-semibold text-primary">{title}</h2>
            <button onClick={onClose} aria-label="Close" className={CLOSE_BTN}>
              <X size={18} />
            </button>
          </div>
        )}
        <div className="flex-1 px-5 py-4 overflow-auto text-sm text-body">{children}</div>
        {footer && <div className={PANEL_FOOTER}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

// ConfirmDialog — built on Modal. variant "default" uses primary, "danger" red.
// Buttons are hand-rolled (Modal doesn't import Button) but matched 1:1 to the
// refined Button primary/danger/outline styles so they look identical.
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
}) {
  const confirmTone =
    variant === "danger"
      ? "bg-danger hover:bg-danger/90 focus-visible:ring-danger"
      : "bg-primary hover:bg-primary-dark focus-visible:ring-primary";
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-btn border border-border bg-white px-4 py-2 text-sm font-medium text-body transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => {
              onConfirm?.();
              onClose?.();
            }}
            className={cx(
              "inline-flex items-center justify-center rounded-btn border border-transparent px-4 py-2 text-sm font-medium text-white shadow-card transition-colors active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              confirmTone
            )}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {message}
    </Modal>
  );
}
